import React, { createContext, useContext, useState, FunctionComponent, PropsWithChildren, useRef, useEffect } from "react";
import { Alert } from "react-bootstrap";
import isString from "lodash/isString";

// --- Context setup ---
type AlertContextType = [string | null, (msg?: string) => void, () => void];

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: FunctionComponent<PropsWithChildren<{}>> = ({ children }) => {
  const [message, setMessage] = useState<string | null>(null);

  const showAlert = (msg?: string) => setMessage(msg ?? null);
  const clearAlert = () => setMessage(null);

  return <AlertContext.Provider value={[message, showAlert, clearAlert]}>{children}</AlertContext.Provider>;
};

// --- Hook to trigger alert ---
export const useAlert = (): ((msg?: string) => void) => {
  const context = useContext(AlertContext);
  if (!context) throw new Error("useAlert must be used within AlertProvider");
  return context[1]; // showAlert
};

// --- Alert component ---
const AlertComponent: FunctionComponent = () => {
  const ref = useRef<HTMLDivElement>(null);
  const context = useContext(AlertContext);

  if (!context) throw new Error("AlertComponent must be used within AlertProvider");

  const [message, , clearAlert] = context;

  // Scroll into view when alert shows
  useEffect(() => {
    if (ref.current && message) ref.current.scrollIntoView({ behavior: "smooth" });
  }, [message]);

  if (!message) return null;

  return (
    <Alert variant="danger" ref={ref} show={isString(message)} onClose={clearAlert} dismissible>
      {message}
    </Alert>
  );
};

export default React.memo(AlertComponent);
